import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageTransition } from "@/components/PageTransition";
import { getAuditPublicConfig } from "@/lib/audit-config.functions";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/get-found/checkout")({
  head: () => ({
    meta: [
      { title: "Checkout — Get Found Playbook | The Outpost Creative" },
      {
        name: "description",
        content:
          "Secure checkout for the Get Found Playbook: a 28-page system, 3 comprehensive audits, and 20 personalized AI prompts. One-time $47.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: () => getAuditPublicConfig(),
  component: CheckoutRoute,
});

/**
 * Embedded checkout frame. Renders the order-form URL in an iframe that
 * auto-resizes when the embed posts a height message; falls back to a fixed
 * height that fits both 2-step views at 375px with no inner scrollbar.
 */
function CheckoutFrame({ src }: { src: string }) {
  const [height, setHeight] = useState(760);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as unknown;
      if (
        d &&
        typeof d === "object" &&
        typeof (d as { height?: unknown }).height === "number"
      ) {
        setHeight(Math.max((d as { height: number }).height, 760));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  return (
    <iframe
      src={src}
      title="Secure checkout"
      className="w-full"
      style={{ height, minHeight: 760, border: "none" }}
      loading="lazy"
    />
  );
}

function CheckoutRoute() {
  const config = Route.useLoaderData();
  return (
    <PageTransition>
      <div className="min-h-screen bg-[#0a0a0f] text-white selection:bg-[#b85736] selection:text-black">
        <Navbar />
        <main className="pt-24 px-5 md:px-8 py-16 md:py-24">
          <div className="max-w-xl mx-auto">
            <Link
              to="/get-found"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#b85736] transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Back to overview
            </Link>

            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#b85736] font-bold">
              Order
            </span>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-tight text-white">
              Get instant access: $47
            </h1>
            <p className="mt-3 text-white/70 text-sm md:text-base">
              One-time payment. Playbook + 3 comprehensive audits + 20
              personalized prompts.
            </p>

            {config.playbookOrderFormUrl ? (
              <div className="mt-8 bg-black/50 border border-white/10 p-2 rounded-xl">
                <CheckoutFrame src={config.playbookOrderFormUrl} />
                <p className="flex items-center justify-center gap-2 font-mono text-xs text-white/50 py-3">
                  <ShieldCheck className="h-4 w-4 text-[#b85736]" /> Secure
                  checkout · Instant access
                </p>
              </div>
            ) : (
              <div className="mt-8 bg-black/50 border border-white/10 p-8 text-center space-y-3 rounded-xl">
                <ShieldCheck className="h-8 w-8 text-[#b85736] mx-auto" />
                <p className="font-heading font-extrabold text-lg uppercase tracking-tight text-white">
                  Checkout opening soon
                </p>
                <p className="font-mono text-xs text-white/60">
                  Our secure checkout is being connected. Check back shortly, or
                  email adarian@theoutpost.agency to grab the playbook now.
                </p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
}
